import React, { useMemo, useState } from 'react';
import { Alert, Button, InputNumber, Modal, Select, Typography } from 'antd';
import { CalculatorOutlined, RightOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { localeForLanguage } from '../../../utils/numberFormatting';
import { formatUnit } from '../../../utils/units';
import {
  asCommercialUnit,
  COMMERCIAL_PRICING_CONVERSION_UNITS,
  COMMERCIAL_UNITS,
  CommercialQuantityValue,
  convertCommercialQuantity,
  roundCommercialQuantity,
} from '../../../utils/commercialQuantity';
import type { AllocationUnit, ConversionSource } from '../../../utils/dimensionConversion';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../../../utils/polishNumberInput';
import './CommercialQuantityEditor.css';

type EntrySide = 'physical' | 'pricing';
type ConversionMode = 'conversion' | 'none';
type RoundingDirection = 'down' | 'up' | null;

interface CommercialQuantityEditorProps extends ConversionSource {
  value: CommercialQuantityValue;
  onChange: (value: CommercialQuantityValue) => void;
  maxPhysicalQuantity: number;
  maxPricingQuantity: number;
  disabled?: boolean;
  compact?: boolean;
  /**
   * The unit the physical quantity is denominated in. Order lines enter in
   * pieces (the default). Warehouse documents pass the product's BASE UNIT
   * instead — WZ/PZ quantities move real stock through FIFO, so the physical
   * side is fixed by the catalog, never chosen in the form.
   */
  physicalUnit?: AllocationUnit;
  /**
   * Hide the "no conversion" unit picker. Set for warehouse documents, where
   * changing the unit would redenominate a stock movement.
   */
  lockPhysicalUnit?: boolean;
  /** Warehouse documents allow inverse m²/m³ -> pieces pricing. */
  pricingConversionUnits?: readonly AllocationUnit[];
}

const positive = (value: unknown, fallback = 1) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const CommercialQuantityEditor: React.FC<CommercialQuantityEditorProps> = ({
  value,
  onChange,
  maxPhysicalQuantity,
  maxPricingQuantity,
  disabled = false,
  compact = false,
  physicalUnit = 'pcs',
  lockPhysicalUnit = false,
  pricingConversionUnits = COMMERCIAL_PRICING_CONVERSION_UNITS,
  width,
  length,
  height,
  thickness,
}) => {
  const { t, i18n } = useTranslation(['orders', 'common']);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<ConversionMode>('none');
  const [entrySide, setEntrySide] = useState<EntrySide>('physical');
  const [pricingUnit, setPricingUnit] = useState<AllocationUnit>('m2');
  const [plainUnit, setPlainUnit] = useState<AllocationUnit>('pcs');
  const [amount, setAmount] = useState(1);
  const [rounding, setRounding] = useState<RoundingDirection>(null);

  const dimensions = useMemo(() => ({ width, length, height, thickness }), [height, length, thickness, width]);
  const isConverted = value.physicalUnit !== value.pricingUnit;
  // Converting a unit into itself is not a conversion. Order forms keep their
  // historical m²/m³ choices; warehouse callers additionally pass `pcs` so an
  // area- or volume-stocked product can still be priced per piece.
  const pricingUnitOptions = useMemo(
    () => pricingConversionUnits.filter((unit) => unit !== physicalUnit),
    [physicalUnit, pricingConversionUnits],
  );
  // Rounding to a whole unit only means something for a countable one. Fractions
  // of a square metre are perfectly ordinary.
  const supportsWholeUnitRounding = physicalUnit === 'pcs';

  const openEditor = () => {
    // `value.physicalUnit === physicalUnit` is what keeps ORDER and SUPPLIER
    // lines on their original behaviour. Those callers do not pass the prop, so
    // it defaults to 'pcs' — and a line legitimately stored as unit='m2' with
    // saleUnit='pcs' must still open in plain mode. Dropping this guard opened
    // it in conversion mode with the physical side pinned to szt., so pressing
    // Apply relabelled m² as pieces WITHOUT converting: the exact corruption
    // this whole change exists to prevent, reintroduced on the sales side.
    const nextMode: ConversionMode = value.physicalUnit === physicalUnit
      && value.pricingUnit !== value.physicalUnit
      ? 'conversion'
      : 'none';
    const fallbackPricingUnit = pricingUnitOptions[0] ?? 'm2';
    setMode(nextMode);
    setEntrySide('physical');
    setPricingUnit(
      pricingUnitOptions.includes(value.pricingUnit) ? value.pricingUnit : fallbackPricingUnit,
    );
    setPlainUnit(asCommercialUnit(value.physicalUnit, physicalUnit));
    setAmount(positive(value.physicalQuantity));
    setRounding(null);
    setOpen(true);
  };

  const baseResult = useMemo(() => {
    if (mode === 'none') {
      return {
        physicalQuantity: positive(amount),
        physicalUnit: plainUnit,
        pricingQuantity: positive(amount),
        pricingUnit: plainUnit,
        error: null,
        theoreticalPhysical: positive(amount),
      };
    }

    if (entrySide === 'physical') {
      const converted = convertCommercialQuantity(positive(amount), physicalUnit, pricingUnit, dimensions);
      return {
        physicalQuantity: positive(amount),
        physicalUnit,
        pricingQuantity: converted.value ?? 0,
        pricingUnit,
        error: converted.error,
        theoreticalPhysical: positive(amount),
      };
    }

    const converted = convertCommercialQuantity(positive(amount), pricingUnit, physicalUnit, dimensions);
    return {
      physicalQuantity: converted.value ?? 0,
      physicalUnit,
      pricingQuantity: positive(amount),
      pricingUnit,
      error: converted.error,
      theoreticalPhysical: converted.exactValue ?? 0,
    };
  }, [amount, dimensions, entrySide, mode, physicalUnit, plainUnit, pricingUnit]);

  const result = useMemo(() => {
    if (mode !== 'conversion' || entrySide !== 'pricing' || !rounding || baseResult.error) {
      return baseResult;
    }

    const physicalQuantity = rounding === 'up'
      ? Math.ceil(baseResult.theoreticalPhysical)
      : Math.floor(baseResult.theoreticalPhysical);
    const converted = convertCommercialQuantity(physicalQuantity, physicalUnit, pricingUnit, dimensions);
    return {
      ...baseResult,
      physicalQuantity,
      pricingQuantity: converted.value ?? 0,
      error: converted.error,
    };
  }, [baseResult, dimensions, entrySide, mode, physicalUnit, pricingUnit, rounding]);

  const hasFractionalPieces = supportsWholeUnitRounding
    && mode === 'conversion'
    && entrySide === 'pricing'
    && !baseResult.error
    && Math.abs(baseResult.theoreticalPhysical - Math.round(baseResult.theoreticalPhysical)) > 0.0001;
  const downPieces = Math.floor(baseResult.theoreticalPhysical);
  const upPieces = Math.ceil(baseResult.theoreticalPhysical);
  const downPricing = convertCommercialQuantity(downPieces, physicalUnit, pricingUnit, dimensions).value;
  const upPricing = convertCommercialQuantity(upPieces, physicalUnit, pricingUnit, dimensions).value;

  // "Podaję sztuki" only reads well when the physical side IS pieces. A
  // warehouse line denominated in m²/m³ gets the unit-parameterised wording.
  const physicalUnitCopy = supportsWholeUnitRounding
    ? {
      enterLabel: t('orders:unitConversion.enterPieces'),
      enterHelp: t('orders:unitConversion.enterPiecesHelp'),
      amountLabel: t('orders:unitConversion.piecesAmount'),
      // "System obliczy liczbę sztuk" is only true when pieces are the other side.
      pricingHelp: t('orders:unitConversion.enterPricingHelp'),
    }
    : {
      enterLabel: t('orders:unitConversion.enterPricing', { unit: formatUnit(physicalUnit, i18n.language) }),
      enterHelp: t('orders:unitConversion.enterBaseHelp'),
      amountLabel: t('orders:unitConversion.pricingAmount', { unit: formatUnit(physicalUnit, i18n.language) }),
      pricingHelp: t('orders:unitConversion.enterBaseHelp'),
    };

  const formatQuantity = (quantity: number) => Number(quantity).toLocaleString(
    localeForLanguage(i18n.language),
    { maximumFractionDigits: 3 },
  );

  const amountMaximum = mode === 'conversion' && entrySide === 'pricing'
    ? maxPricingQuantity
    : maxPhysicalQuantity;
  const resultOutOfBounds = result.physicalQuantity > maxPhysicalQuantity
    || result.pricingQuantity > maxPricingQuantity;
  const canApply = !result.error
    && !resultOutOfBounds
    && result.physicalQuantity > 0
    && result.pricingQuantity > 0;

  const handleApply = () => {
    if (!canApply) return;
    onChange({
      physicalQuantity: roundCommercialQuantity(result.physicalQuantity),
      physicalUnit: result.physicalUnit,
      pricingQuantity: roundCommercialQuantity(result.pricingQuantity),
      pricingUnit: result.pricingUnit,
    });
    setOpen(false);
  };

  return (
    <>
      <div className={`commercial-quantity${compact ? ' commercial-quantity--compact' : ''}`}>
        {isConverted ? (
          <div className="commercial-quantity__chain">
            <div><span>{t('orders:unitConversion.physicalQuantity')}</span><strong>{formatQuantity(value.physicalQuantity)} {formatUnit(value.physicalUnit, i18n.language)}</strong></div>
            <RightOutlined />
            <div className="commercial-quantity__pricing"><span>{t('orders:unitConversion.pricingQuantity')}</span><strong>{formatQuantity(value.pricingQuantity)} {formatUnit(value.pricingUnit, i18n.language)}</strong></div>
          </div>
        ) : (
          <strong className="commercial-quantity__plain">
            {formatQuantity(value.physicalQuantity)} {formatUnit(value.physicalUnit, i18n.language)}
          </strong>
        )}
        {!disabled && (
          <Button type="link" size="small" icon={<CalculatorOutlined />} onClick={openEditor}>
            {isConverted ? t('orders:unitConversion.change') : t('orders:unitConversion.configure')}
          </Button>
        )}
      </div>

      <Modal
        open={open}
        width={720}
        title={t('orders:unitConversion.title')}
        okText={t('orders:unitConversion.apply')}
        cancelText={t('common:cancel')}
        okButtonProps={{ disabled: !canApply }}
        onOk={handleApply}
        onCancel={() => setOpen(false)}
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary" className="commercial-quantity-modal__intro">
          {t('orders:unitConversion.description')}
        </Typography.Paragraph>

        <span className="commercial-quantity-modal__label">{t('orders:unitConversion.mode')}</span>
        <div className="commercial-quantity-modal__choices">
          <button type="button" className={mode === 'conversion' ? 'active' : ''} onClick={() => { setMode('conversion'); setRounding(null); }}>
            <strong>{t('orders:unitConversion.convert')}</strong><small>{t('orders:unitConversion.convertHelp')}</small>
          </button>
          <button type="button" className={mode === 'none' ? 'active' : ''} onClick={() => { setMode('none'); setRounding(null); }}>
            <strong>{t('orders:unitConversion.noConversion')}</strong><small>{t('orders:unitConversion.noConversionHelp')}</small>
          </button>
        </div>

        {mode === 'none' ? (
          <div className="commercial-quantity-modal__fields">
            <label><span>{t('orders:unitConversion.quantity')}</span><InputNumber min={0.001} max={maxPhysicalQuantity} precision={3} step={0.001} {...POLISH_DECIMAL_INPUT_NUMBER_PROPS} value={amount} onChange={(next) => setAmount(positive(next))} /></label>
            <label>
              <span>{t('orders:unitConversion.unit')}</span>
              <Select
                value={plainUnit}
                disabled={lockPhysicalUnit}
                options={(lockPhysicalUnit ? [physicalUnit] : COMMERCIAL_UNITS).map((unit) => ({ value: unit, label: formatUnit(unit, i18n.language) }))}
                onChange={setPlainUnit}
              />
            </label>
          </div>
        ) : (
          <>
            <div className="commercial-quantity-modal__fields">
              <label><span>{t('orders:unitConversion.physicalUnit')}</span><Select disabled value={physicalUnit} options={[{ value: physicalUnit, label: formatUnit(physicalUnit, i18n.language) }]} /></label>
              <label><span>{t('orders:unitConversion.pricingUnit')}</span><Select value={pricingUnit} options={pricingUnitOptions.map((unit) => ({ value: unit, label: formatUnit(unit, i18n.language) }))} onChange={(next) => { setPricingUnit(next); setRounding(null); }} /></label>
            </div>

            <span className="commercial-quantity-modal__label">{t('orders:unitConversion.entryQuestion')}</span>
            <div className="commercial-quantity-modal__choices">
              <button type="button" className={entrySide === 'physical' ? 'active' : ''} onClick={() => { setEntrySide('physical'); setAmount(positive(value.physicalQuantity)); setRounding(null); }}>
                <strong>{physicalUnitCopy.enterLabel}</strong><small>{physicalUnitCopy.enterHelp}</small>
              </button>
              <button type="button" className={entrySide === 'pricing' ? 'active' : ''} onClick={() => { setEntrySide('pricing'); setAmount(positive(value.pricingQuantity)); setRounding(null); }}>
                <strong>{t('orders:unitConversion.enterPricing', { unit: formatUnit(pricingUnit, i18n.language) })}</strong><small>{physicalUnitCopy.pricingHelp}</small>
              </button>
            </div>

            <label className="commercial-quantity-modal__amount">
              <span>{entrySide === 'physical' ? physicalUnitCopy.amountLabel : t('orders:unitConversion.pricingAmount', { unit: formatUnit(pricingUnit, i18n.language) })}</span>
              <InputNumber min={0.001} max={amountMaximum} precision={3} step={0.001} {...POLISH_DECIMAL_INPUT_NUMBER_PROPS} value={amount} onChange={(next) => { setAmount(positive(next)); setRounding(null); }} />
            </label>

            {result.error ? (
              <Alert type="warning" showIcon message={t('orders:unitConversion.missingDimensions')} />
            ) : resultOutOfBounds ? (
              <Alert type="warning" showIcon message={t('orders:unitConversion.quantityLimit')} />
            ) : (
              <div className="commercial-quantity-modal__equation">
                <div><span>{t('orders:unitConversion.physicalQuantity')}</span><strong>{formatQuantity(result.physicalQuantity)} {formatUnit(result.physicalUnit, i18n.language)}</strong></div>
                <RightOutlined />
                <div><span>{t('orders:unitConversion.pricingQuantity')}</span><strong>{formatQuantity(result.pricingQuantity)} {formatUnit(result.pricingUnit, i18n.language)}</strong></div>
              </div>
            )}

            {hasFractionalPieces && (
              <div className="commercial-quantity-modal__rounding">
                <div className="commercial-quantity-modal__rounding-head"><strong>{t('orders:unitConversion.fractionalTitle')}</strong><span>{formatQuantity(baseResult.pricingQuantity)} {formatUnit(pricingUnit, i18n.language)} = {formatQuantity(baseResult.theoreticalPhysical)} {formatUnit('pcs', i18n.language)}</span></div>
                <p>{t('orders:unitConversion.fractionalDescription')}</p>
                <div className="commercial-quantity-modal__rounding-options">
                  <button type="button" disabled={downPieces <= 0} className={rounding === 'down' ? 'active' : ''} onClick={() => setRounding(rounding === 'down' ? null : 'down')}>
                    <span>{t('orders:unitConversion.roundDown')}</span><small>{formatQuantity(downPricing || 0)} {formatUnit(pricingUnit, i18n.language)}</small><strong>{downPieces} {formatUnit('pcs', i18n.language)}</strong>
                  </button>
                  <button type="button" className={rounding === 'up' ? 'active' : ''} onClick={() => setRounding(rounding === 'up' ? null : 'up')}>
                    <span>{t('orders:unitConversion.roundUp')}</span><small>{formatQuantity(upPricing || 0)} {formatUnit(pricingUnit, i18n.language)}</small><strong>{upPieces} {formatUnit('pcs', i18n.language)}</strong>
                  </button>
                </div>
                {!rounding && <p className="commercial-quantity-modal__rounding-optional">{t('orders:unitConversion.roundingOptional')}</p>}
              </div>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default CommercialQuantityEditor;
